import * as React from 'react';
import { cn } from '@/lib/cn';
import { IconChevronLeft, IconCross } from '@repo/svgs';

export interface AppBarProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Main title shown in the middle */
  title: string;
  subtitle?: string;
  /** Optional leading slot (e.g., back button). If omitted and `onBack` provided, a default back button renders. */
  leading?: React.ReactNode;
  /** Optional trailing slot (e.g., close button). If omitted and `onClose` provided, a default close button renders. */
  trailing?: React.ReactNode;
  /** Click handler to show a default back icon button on the left. */
  onBack?: () => void;
  /** Click handler to show a default close icon button on the right. */
  onClose?: () => void;
}

export function AppBar({
  title,
  subtitle,
  leading,
  trailing,
  onBack,
  onClose,
  className,
  ...rest
}: AppBarProps) {
  const hasLeading = Boolean(leading) || Boolean(onBack);
  const hasTrailing = Boolean(trailing) || Boolean(onClose);
  const containerPaddingClassName = hasLeading
    ? hasTrailing
      ? 'px-4'
      : 'pl-4 pr-6'
    : hasTrailing
      ? 'pl-6 pr-4'
      : 'px-6';

  return (
    <div
      className={cn('w-full', className, 'border-b border-secondary')}
      role="banner"
      aria-label="App bar"
      {...rest}
    >
      <div className={cn('flex items-center', 'py-4', containerPaddingClassName)}>
        {/* Leading */}
        {hasLeading ? (
          <div className="shrink-0">
            <button
              type="button"
              aria-label="Go back"
              onClick={onBack}
              className={cn(
                'rounded-full p-3 text-secondary hover:bg-primary-hover cursor-pointer',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2',
              )}
            >
              <IconChevronLeft width={16} height={16} className="icon-secondary" />
            </button>
          </div>
        ) : null}

        <div className="flex flex-1 flex-col gap-1">
          {/* Title */}
          <h5 className="truncate text-body-1-high text-primary flex-1">{title}</h5>

          {/* Subtitle */}
          {subtitle && <p className="text-body-2 text-secondary truncate">{subtitle}</p>}
        </div>

        {/* Trailing */}
        <div className="shrink-0">
          {hasTrailing ? (
            <button
              type="button"
              aria-label="Close"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                onClose?.();
              }}
              className={cn(
                'rounded-full p-3 text-secondary hover:bg-primary-hover cursor-pointer',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2',
              )}
            >
              <IconCross className="icon-secondary size-4" />
            </button>
          ) : (
            // Mirror leading spacing if missing
            <div className="w-10" aria-hidden />
          )}
        </div>
      </div>
    </div>
  );
}

export default AppBar;
